import React, { useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  DOLLAR_SIGN,
  ESCAPED_DOLLAR,
  INPUT_EVENT,
  MATH_FIELD,
  USER_TYPE
} from 'utils/constants';
import { traverseNodes } from 'utils/latexRenderer';

import styles from './styles.module.scss';

const BlockEmbed = Quill.import('blots/block/embed');

class MathBlot extends BlockEmbed {
  static blotName = 'math';
  static tagName = 'span';
  static className = 'math-field-wrapper';

  static create(value) {
    const node = super.create();
    const mathField = document.createElement(MATH_FIELD);
    mathField.value = value;
    mathField.className = styles.inlineMath;
    node.appendChild(mathField);

    // Live update math content
    mathField.addEventListener(INPUT_EVENT, () => {
      node.dataset.value = mathField.value;
    });

    node.dataset.value = value;
    return node;
  }

  static value(node) {
    return node.dataset.value || '';
  }
}

Quill.register(MathBlot);

const icons = Quill.import('ui/icons');
icons['insertMath'] = '∑';

// ---------------------------------------

const EditorField = ({ initialValue = '', onChange, readOnly = false }) => {
  const quillRef = useRef(null);
  const editorRef = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    editorRef.current = editor;

    if (!mounted.current) {
      // Normalize backslashes
      const normalizedValue = initialValue
        .replace(/\\\\\\\\/g, '\\\\')
        .replace(/\\n/g, '\\n');

      const protectedValue = normalizedValue.replace(/\\\$/g, ESCAPED_DOLLAR);

      // Split ONLY on real math delimiters
      const lines = protectedValue.split(/(\$[^$]+\$)/g);

      editor.setContents([]); // empties the editor fully

      // Move cursor to the end so Quill doesn't push content down
      editor.setSelection(editor.getLength(), 0);

      lines.forEach((line, index) => {
        if (line === '\\n') {
          editor.insertText(editor.getLength(), '\n', 'silent');
          return;
        }

        if (
          (line === '' || line === ',' || line === '.') &&
          index === lines.length - 1
        ) {
          editor.insertText(editor.getLength(), '\n', 'silent');
          return;
        }

        // Math field (only real $...$)
        if (line.startsWith(DOLLAR_SIGN) && line.endsWith(DOLLAR_SIGN)) {
          const latex = line.slice(1, -1).replace(/\\\\/g, '\\');

          if (editor.getLength() === 1) {
            editor.deleteText(0, 1);
            editor.insertEmbed(0, 'math', latex);
          } else {
            editor.insertEmbed(editor.getLength(), 'math', latex);
          }
        } else {
          // Restore escaped dollars
          const restored = line
            .replace(new RegExp(ESCAPED_DOLLAR, 'g'), '$')
            .replace(/^[.,\s]+/, '');

          if (!restored) return;

          if (editor.getLength() === 1) {
            editor.deleteText(0, 1);
            editor.insertText(0, restored);
          } else {
            editor.insertText(editor.getLength(), restored);
          }
        }
      });

      mounted.current = true;
    }
  }, [initialValue]);

  const handleChange = ({ source }) => {
    if (source !== USER_TYPE) return;

    const editor = editorRef.current;
    if (!editor || !editor.root) return;

    const updated = traverseNodes({ node: editor.root });
    onChange?.(updated);
  };

  const modules = useMemo(() => {
    if (readOnly) return false;

    return {
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean'],
          [{ formula: [] }],
          ['insertMath'] // custom button key
        ],
        handlers: {
          insertMath: () => {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;

            const length = editor.getLength();
            editor.insertEmbed(length, 'math', '');
            editor.insertText(editor.getLength(), ' ');

            setTimeout(() => {
              editor.setSelection(editor.getLength(), 0);
            }, 0);
          }
        }
      }
    };
  }, [readOnly]);

  return (
    <ReactQuill
      ref={quillRef}
      readOnly={readOnly}
      theme={readOnly ? 'bubble' : 'snow'}
      onChange={(content, delta, source) => {
        handleChange({ content, delta, source });
      }}
      modules={modules}
      placeholder="Type text with markup and math..."
    />
  );
};

export default EditorField;
